import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useStore } from '../../../store'
import { useThemeStore } from '@/store/ConfigStore';
import { useToast } from '@/components/Toast';
import { validateNumericID } from '@/utils/validator';
import type { Dayjs } from 'dayjs';
import { Table, Drawer, Input } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Pagination } from "@/components/Pagination";
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import { MobileTableCard } from '@/components/MobileCards';
import type { Transaction, CumulativeTransaction } from '@/types/financialDetails';
import { fetchTransactionList, exportData, fetchContractCode } from '@/services/financialDetails';
import { fetchGroupID } from '@/services/customerManagement';
import { GroupId } from '@/types/customerManagement';
import dayjs from 'dayjs';
import CopyIcon from '../../../assets/svg/copy.svg';
import CopyDarkIcon from '../../../assets/svg/copyDark.svg';
import CheckIcon from '../../../assets/svg/check-line.svg';
import CheckDarkIcon from '../../../assets/svg/check-line-dark.svg';


export const routeConfig: RouteConfig = {
    title: 'route.financialDetails.transaction',
    layout: true,
    sort: 1
};

const Transaction = () => {
    const { t } = useLocale();
    const { UserStore } = useStore();
    const isDark = useThemeStore(); // 引入主题状态
    const toast = useToast();

    // const [activeTab, setActiveTab] = useState<'spot' | 'futures' | 'alpha'>('spot');
    const [customerUID, setCustomerUID] = useState('');
    const [superiorAgentUID, setSuperiorAgentUID] = useState('');
    const [contractCodes, setContractCodes] = useState<string[]>([]);
    const [tradingPair, setTradingPair] = useState(0);
    const [customerGroups, setCustomerGroups] = useState<GroupId[]>([]);
    const [groupID, setGroupID] = useState<number>(0);
    const [role, setRole] = useState(null);
    const [transactionTime, setTransactionTime] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [transactionMethod, setTransactionMethod] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(1689081600);
    const [transactionData, setTransactionData] = useState<Transaction[]>([]);
    const [cumulativeData, setCumulativeData] = useState<CumulativeTransaction | null>(null);
    const [copiedUID, setCopiedUID] = useState<string | null>(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);

    const pageType = 2; // 1 - commission, 2 - transaction, 3 - summary

    const buildTransactionParams = (customerUID: string, superiorAgentUID: string, tradingPair: number, groupID: number, role: number | null, transactionTime: [Dayjs | null, Dayjs | null], transactionMethod: number) => {
        let params: any = {};
        if (customerUID) {
            params.user_id = customerUID;
        }
        if (superiorAgentUID) {
            params.superior_agent_id = superiorAgentUID;
        }
        if (tradingPair !== 0 && tradingPair !== null) {
            params.contract_code = tradingPair;
        }
        if (groupID !== 0 && groupID !== null) {
            params.group_id = Number(groupID);
        }
        if (role !== null) {
            params.is_maker = Number(role);
        }
        if (transactionTime[0] && transactionTime[1]) {
            params.time_range = [transactionTime[0].unix(), transactionTime[1].unix()];
        }
        if (transactionMethod !== 0 && transactionMethod !== null) {
            params.trading_method = transactionMethod;
        }
        params.agent_id = UserStore.userInfo?.id;
        params.invite_code = UserStore.userInfo?.inviteCode;
        return params;
    };

    const handleReset = async () => {
        try {
            setCustomerUID('');
            setSuperiorAgentUID('');
            setTradingPair(0);
            setGroupID(0);
            setRole(null);
            setTransactionTime([null, null]);
            setTransactionMethod(0);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            const params = buildTransactionParams(customerUID, superiorAgentUID, tradingPair, groupID, role, transactionTime, transactionMethod);
            const res = await fetchTransactionList(params, 1, pagination.pageSize);
            setTransactionData(res.list);
            setTotal(res.total);
            setCumulativeData({
                cumulativeTradingVolume: res.total_trading_volume,
                spotVolume: res.spot_trading_volume,
                futuresVolume: res.contract_trading_volume,
                alphaVolume: res.alpha_trading_volume,
                cumulativeTransactionFees: res.total_handling_fees,
                spotFees: res.spot_handling_fees,
                futuresFees: res.contract_handling_fees,
                alphaFees: res.alpha_handling_fees,
            });
        } catch (error: any) {
            toast.error({
                message: error?.message || t('financialDetails.transaction.fetchError') || 'An error occurred while resetting.'
            });
        }
    };

    const handleQuery = async () => {
        try {
            // Validate customerUID and superiorAgentUID before querying
            if (customerUID.trim() !== '') {
                const validation = validateNumericID(customerUID, 'UID');
                if (!validation.isValid) {
                    toast.error({
                        message: t(validation.message)
                    });
                    return;
                }
            }

            if (superiorAgentUID.trim() !== '') {
                const validation = validateNumericID(superiorAgentUID, 'agentID');
                if (!validation.isValid) {
                    toast.error({
                        message: t(validation.message)
                    });
                    return;
                }
            }

            const params = buildTransactionParams(customerUID, superiorAgentUID, tradingPair, groupID, role, transactionTime, transactionMethod);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            const res = await fetchTransactionList(params, 1, pagination.pageSize);
            setTransactionData(res.list);
            setTotal(res.total);
            setCumulativeData({
                cumulativeTradingVolume: res.total_trading_volume,
                spotVolume: res.spot_trading_volume,
                futuresVolume: res.contract_trading_volume,
                alphaVolume: res.alpha_trading_volume,
                cumulativeTransactionFees: res.total_handling_fees,
                spotFees: res.spot_handling_fees,
                futuresFees: res.contract_handling_fees,
                alphaFees: res.alpha_handling_fees,
            });
        } catch (error: any) {
            toast.error({
                message: error?.message || t('financialDetails.transaction.fetchError') || 'An error occurred while querying.'
            });
        }
    };

    function exportToCsv(filename: string, rows: any[]) {
        if (!rows.length) return;
        const keys = Object.keys(rows[0]);
        const csvContent =
            keys.join(',') + '\n' +
            rows.map(row => keys.map(k => `"${row[k] ?? ''}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const handleExport = async () => {
        try {
            console.log('Exporting data...');
            setLoading(true);
            const params = buildTransactionParams(customerUID, superiorAgentUID, tradingPair, groupID, role, transactionTime, transactionMethod);
            const result = await exportData(params, pageType);
            
            // Export the data list to CSV
            if (result && result.list && result.list.length > 0) {
                exportToCsv('transaction_data.csv', result.list);
                toast.success({
                    message: t('financialDetails.transaction.exportSuccess') || 'Data exported successfully!'
                });
            } else {
                toast.warning({
                    message: t('financialDetails.transaction.noDataToExport') || 'No data to export.'
                });
            }
        } catch (error: any) {
            console.error('Export failed:', error);
            toast.error({
                message: error?.message || t('financialDetails.transaction.exportError') || 'Export failed. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    }

    const formatEmail = (email?: string) => {
        if (!email) return t('financialDetails.transaction.none');
        // Show only the first 3 and last 3 characters of the whole email, mask the rest with '***'
        if (email.length <= 6) {
            return email[0] + '***' + email[email.length - 1];
        }
        const first = email.slice(0, 3);
        const last = email.slice(-3);
        return first + '***' + last;
    };

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('invitationPromotion.invitationLink.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const formatNumber = (num: number | string | null | undefined) => {
        if (num === null || num === undefined) return '--';
        if (isNaN(Number(num))) return '--';
        // 1000000.023024 = 1,000,000.02
        return Number(num).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    };

    const handleCopyUID = async (uid: string) => {
        try {
            await navigator.clipboard.writeText(uid);
            setCopiedUID(uid);
            setTimeout(() => setCopiedUID(null), 2000);
        } catch (err) {
            // handle error
        }
    };

    const queryFields: QueryField[] = [
        {
            type: 'input',
            name: 'customerUID',
            label: t('financialDetails.transaction.customerUID'),
            value: customerUID,
            onChange: setCustomerUID,
            placeholder: t('financialDetails.transaction.customerPlaceholder'),
        },
        {
            type: 'input',
            name: 'superiorAgentUID',
            label: t('financialDetails.transaction.superiorAgentUID'),
            value: superiorAgentUID,
            onChange: setSuperiorAgentUID,
            placeholder: t('financialDetails.transaction.superiorAgentPlaceholder'),
        },
        {
            type: 'select',
            name: 'tradingPair',
            label: t('financialDetails.transaction.tradingPair'),
            value: tradingPair,
            onChange: setTradingPair,
            options: [
                { label: t('financialDetails.transaction.all'), value: null },
                ...contractCodes.map(code => ({ label: code, value: code }))
            ]
        },
        // {
        //     type: 'select',
        //     name: 'groupName',
        //     label: t('financialDetails.transaction.groupName'),
        //     value: groupID,
        //     onChange: setGroupID,
        //     options: [
        //         { label: t('financialDetails.transaction.all'), value: null },
        //         ...customerGroups.map(group => ({
        //             value: group.groupId,
        //             label: group.groupName
        //         }))
        //     ]
        // },
        {
            type: 'select',
            name: 'role',
            label: t('financialDetails.transaction.role'),
            value: role,
            onChange: setRole,
            options: [
                { label: t('financialDetails.transaction.all'), value: null },
                { label: t('financialDetails.transaction.taker'), value: '0' },
                { label: t('financialDetails.transaction.maker'), value: '1' },
            ]
        },
        {
            type: 'dateRange',
            name: 'transactionTime',
            label: t('financialDetails.transaction.transactionTime'),
            value: transactionTime,
            onChange: (dates: [Dayjs | null, Dayjs | null] | null) => {
                setTransactionTime(dates ?? [null, null]);
            },
            placeholder: [t('financialDetails.transaction.startTime'), t('financialDetails.transaction.endTime')],
        },
        {
            type: 'select',
            name: 'transactionMethod',
            label: t('financialDetails.transaction.transactionMethod'),
            value: transactionMethod,
            onChange: (value) => setTransactionMethod(value),
            options: [
                { label: t('financialDetails.transaction.all'), value: null },
                { label: t('financialDetails.transaction.buy'), value: 'B' },
                { label: t('financialDetails.transaction.sell'), value: 'S' },
            ]
        }
    ];

    const transactionColumns: ColumnsType<Transaction> = [
        {
            title: t('financialDetails.transaction.table.customerUID'),
            dataIndex: 'customerUID',
            key: 'customerUID',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text: string) => (
                <div className="flex items-center gap-1">
                    <span>{text}</span>
                    <button
                        className="border-none bg-transparent p-0 m-0"
                        onClick={() => handleCopyUID(text)}
                    >
                        <img
                            src={copiedUID === text ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)}
                            alt={copiedUID === text ? 'copied' : 'copy'}
                            className="w-4 h-4"
                        />
                    </button>
                </div>
            ),
        },
        {
            title: t('financialDetails.transaction.table.remark'),
            dataIndex: 'remark',
            key: 'remark',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text: string) => text || '--'
        },
        {
            title: t('financialDetails.transaction.table.orderID'),
            dataIndex: 'orderID',
            key: 'orderID',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text: string) => (
                <div className="flex items-center gap-1">
                    <span>{text}</span>
                    <button
                        className="border-none bg-transparent p-0 m-0"
                        onClick={() => handleCopyUID(text)}
                    >
                        <img
                            src={copiedUID === text ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)}
                            alt={copiedUID === text ? 'copied' : 'copy'}
                            className="w-4 h-4"
                        />
                    </button>
                </div>
            ),
        },
        {
            title: t('financialDetails.transaction.table.email'),
            dataIndex: 'email',
            key: 'email',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text: string) => formatEmail(text)
        },
        // {
        //     title: t('financialDetails.transaction.table.handlingFee'),
        //     dataIndex: 'handlingFee',
        //     key: 'handlingFee',
        //     align: 'start',
        //     width: 120,
        //     ellipsis: true,
        //     render: (text) => text || '--'
        // },
        // {
        //     title: t('financialDetails.transaction.table.group'),
        //     dataIndex: 'group',
        //     key: 'group',
        //     align: 'start',
        //     width: 100,
        //     ellipsis: true,
        //     render: (text) => text || '--'
        // },
        {
            title: t('financialDetails.transaction.table.superiorAgentUID'),
            dataIndex: 'superiorAgentUID',
            key: 'superiorAgentUID',
            align: 'start',
            width: 140,
            ellipsis: true,
            render: (text) => text || '--'
        },
        {
            title: t('financialDetails.transaction.table.transactionTime'),
            dataIndex: 'transactionTime',
            key: 'transactionTime',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text) => formatDate(text)
        },
        {
            title: t('financialDetails.transaction.table.tradingPair'),
            dataIndex: 'tradingPair',
            key: 'tradingPair',
            align: 'start',
            width: 100,
            ellipsis: true,
        },
        {
            title: t('financialDetails.transaction.table.transactionVolume'),
            dataIndex: 'transactionVolume',
            key: 'transactionVolume',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => formatNumber(text)
        },
        {
            title: t('financialDetails.transaction.table.netFee'),
            dataIndex: 'netFee',
            key: 'netFee',
            align: 'start',
            width: 100,
            ellipsis: true,
            render: (text) => (text === null || text === undefined || text === '') ? '--' : text
        },
        {
            title: t('financialDetails.transaction.table.transactionMethod'),
            dataIndex: 'transactionMethod',
            key: 'transactionMethod',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => text === 'B' ? t('financialDetails.commission.buy') : t('financialDetails.commission.sell')
        },
    ];

    const mobileTransactionColumns = transactionColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                try {
                    const groupRes = await fetchGroupID({});
                    setCustomerGroups(groupRes);
                    const contractRes = await fetchContractCode();
                    setContractCodes(contractRes);
                } catch (groupError: any) {
                    toast.error({
                        message: groupError?.message || t('customerManagement.clientList.groupFetchError') || 'An error occurred while fetching group list.'
                    });
                }

                try {
                    const params = buildTransactionParams(customerUID, superiorAgentUID, tradingPair, groupID, role, transactionTime, transactionMethod);
                    const res = await fetchTransactionList(params, pagination.current, pagination.pageSize);
                    setTransactionData(res.list);
                    setTotal(res.total);
                    setCumulativeData({
                        cumulativeTradingVolume: res.total_trading_volume,
                        spotVolume: res.spot_trading_volume,
                        futuresVolume: res.contract_trading_volume,
                        alphaVolume: res.alpha_trading_volume,
                        cumulativeTransactionFees: res.total_handling_fees,
                        spotFees: res.spot_handling_fees,
                        futuresFees: res.contract_handling_fees,
                        alphaFees: res.alpha_handling_fees,
                    });
                } catch (error: any) {
                    toast.error({
                        message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching client list.'
                    });
                }
                setLastUpdated(Math.floor(Date.now() / 1000));
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [pagination.current, pagination.pageSize]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            <GenericQueryCard
                title={t('financialDetails.transaction.title')}
                subtitle={t('financialDetails.transaction.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                fields={queryFields}
                onReset={handleReset}
                onQuery={handleQuery}
                onExport={handleExport}
                gridCols="grid-cols-2 md:grid-cols-4"
            />

            <div className="mt-6 w-full grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col p-6 bg-card-background dark:bg-card-background rounded-lg items-start">
                    <div className="w-full text-normal dark:text-dark-normal text-sm font-normal font-inter leading-normal">
                        {t('financialDetails.transaction.cumulativeTradingVolume')}
                    </div>
                    <div className="mt-5 w-full flex text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal">
                        {formatNumber(cumulativeData?.cumulativeTradingVolume || 0)}
                    </div>
                    <div className="mt-4 px-6 py-4 w-full max-w-[465px] flex flex-row bg-button dark:bg-button-dark rounded-lg">
                        <div className="flex flex-col gap-[6px] flex-1">
                            <div className="w-full h-6 items-center flex text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-normal">
                                {formatNumber(cumulativeData?.spotVolume || 0)}
                            </div>
                            <div className="w-full h-6 items-center flex text-normal dark:text-dark-normal text-xs font-normal font-inter leading-normal">
                                {t('financialDetails.transaction.spot')}
                            </div>
                        </div>
                        <div className="flex flex-col  gap-[6px] flex-1">
                            <div className="w-full h-6 items-center flex text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-normal">
                                {formatNumber(cumulativeData?.futuresVolume || 0)}
                            </div>
                            <div className="w-full h-6 items-center flex text-normal dark:text-dark-normal text-xs font-normal font-inter leading-normal">
                                {t('financialDetails.transaction.futures')}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col p-6 bg-card-background dark:bg-card-background rounded-lg items-start">
                    <div className="w-full text-normal dark:text-dark-normal text-sm font-normal font-inter leading-normal">
                        {t('financialDetails.transaction.cumulativeTransactionFees')}
                    </div>
                    <div className="mt-5 w-full text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal">
                        {formatNumber(cumulativeData?.cumulativeTransactionFees || 0)}
                    </div>
                    <div className="mt-4 px-6 py-4 w-full max-w-[465px] flex flex-row bg-button dark:bg-button-dark rounded-lg">
                        <div className="flex flex-col gap-[6px] flex-1">
                            <div className="w-full h-6 items-center flex text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-normal">
                                {formatNumber(cumulativeData?.spotFees || 0)}
                            </div>
                            <div className="w-full h-6 items-center flex text-normal dark:text-dark-normal text-xs font-normal font-inter leading-normal">
                                {t('financialDetails.transaction.spot')}
                            </div>
                        </div>
                        <div className="flex flex-col  gap-[6px] flex-1">
                            <div className="w-full h-6 items-center flex text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-normal">
                                {formatNumber(cumulativeData?.futuresFees || 0)}
                            </div>
                            <div className="w-full h-6 items-center flex text-normal dark:text-dark-normal text-xs font-normal font-inter leading-normal">
                                {t('financialDetails.transaction.futures')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Active Tab for Spot, Futures, and Alpha */}

            {/* Table Container with Custom Layout */}
            <div className="mt-6 bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                {/* Table Content */}
                <div className="hidden md:block h-[556px] overflow-hidden overflow-y-auto overflow-x-auto">
                    <Table
                        dataSource={transactionData}
                        columns={transactionColumns}
                        rowKey={(record) => `${record.customerUID}-${record.transactionTime}-${record.transactionVolume}`}
                        pagination={false}
                        loading={loading}
                        size="small"
                        // scroll={{ x: 850 }}
                        className="custom-table"
                        showHeader={true}
                        locale={{ emptyText: '' }}
                        components={{
                            body: {
                                wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                            }
                        }}
                    />
                </div>
                <div className="block md:hidden">
                    {transactionData.map((row, idx) => (
                        <MobileTableCard
                            key={`${row.customerUID}-${row.transactionTime}-${row.transactionVolume}-${idx}`}
                            record={row}
                            columns={mobileTransactionColumns}
                            t={t}
                        />
                    ))}
                </div>
                <div className="mt-4 flex justify-end">
                    <Pagination
                        currentPage={pagination.current}
                        totalPages={Math.ceil(total / pagination.pageSize)}
                        onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                        pageSize={pagination.pageSize}
                        setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                        pageSizes={[10, 20, 50, 100]}
                        isPageSizeVisible={true}
                        disabled={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default Transaction;
